import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { deleteSchool, getSchools } from '../../services/schoolService';
import { getAllCities, getAllStates } from '../../services/locationService';
import Loader from '../common/Loader';
import ConfirmModal from '../common/ConfirmModal';
import Alert from '../common/Alert';
import TableActionMenu from '../common/TableActionMenu';
import { SCHOOL_TYPES } from '../org/orgFormUtils';

function formatStateLabel(stateCode, stateMap) {
  if (!stateCode) return '—';
  const match = stateMap[stateCode.toUpperCase()];
  if (match) return `${match.state_name} (${match.state_code})`;
  return stateCode;
}

export default function SchoolList() {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [filters, setFilters] = useState({
    school_name: '',
    state_code: '',
    city: '',
    school_type: '',
  });
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, total_pages: 0 });

  const debounceRef = useRef(null);

  const stateMap = useMemo(
    () => Object.fromEntries(states.map((s) => [s.state_code, s])),
    [states],
  );

  useEffect(() => {
    getAllStates()
      .then((res) => setStates(res.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getAllCities(filters.state_code || null)
      .then((res) => setCities(res.data?.data || []))
      .catch(() => setCities([]));
  }, [filters.state_code]);

  const fetchSchools = useCallback(async (params = {}, page = 1) => {
    setLoading(true);
    try {
      const queryParams = { page, limit: pagination.limit, ...params };
      Object.keys(queryParams).forEach((k) => {
        if (queryParams[k] === '' || queryParams[k] == null) delete queryParams[k];
      });
      const { data } = await getSchools(queryParams);
      setSchools(data.data?.schools || []);
      setPagination({
        total: data.data?.total || 0,
        page: data.data?.page || 1,
        limit: data.data?.limit || 10,
        total_pages: data.data?.total_pages || 0,
      });
      setError('');
    } catch {
      setError('Failed to load schools.');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => {
    fetchSchools();
  }, []); // eslint-disable-line

  const handleFilterChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    if (key === 'state_code') {
      updated.city = '';
    }
    setFilters(updated);
    clearTimeout(debounceRef.current);
    if (key === 'school_name') {
      debounceRef.current = setTimeout(() => fetchSchools(updated, 1), 400);
    } else {
      fetchSchools(updated, 1);
    }
  };

  const clearFilters = () => {
    const cleared = {
      school_name: '',
      state_code: '',
      city: '',
      school_type: '',
    };
    setFilters(cleared);
    fetchSchools(cleared, 1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.total_pages) return;
    fetchSchools(filters, page);
  };

  const toggleMenu = (id) => setOpenMenuId((prev) => (prev === id ? null : id));

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteSchool(confirmDelete.id);
      setAlert({ type: 'success', message: 'School deleted successfully.' });
      setConfirmDelete(null);
      fetchSchools(filters, pagination.page);
    } catch (err) {
      setAlert({
        type: 'error',
        message: err.response?.data?.detail || 'Failed to delete school.',
      });
    } finally {
      setDeleting(false);
    }
  };

  const hasActiveFilters = filters.school_name || filters.state_code || filters.city || filters.school_type;

  return (
    <div className="space-y-5 min-w-0">
      {alert && (
        <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title="Delete School"
        message={`Are you sure you want to delete "${confirmDelete?.school_name}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        variant="danger"
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Schools</h1>
          <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">
            Manage schools and LEA sites
          </p>
        </div>
        <Link
          to="/schools/add"
          className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors shadow-lg shadow-indigo-500/20"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add School
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative flex-1 min-w-0 w-full sm:min-w-[180px] sm:max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by school name..."
            value={filters.school_name}
            onChange={(e) => handleFilterChange('school_name', e.target.value)}
            className="w-full pl-9 pr-3.5 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm transition-colors"
          />
        </div>

        <select
          aria-label="State"
          value={filters.state_code}
          onChange={(e) => handleFilterChange('state_code', e.target.value)}
          className="w-full sm:w-auto px-3 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
        >
          <option value="">All States</option>
          {states.map((s) => (
            <option key={s.state_code} value={s.state_code}>
              {s.state_name}
            </option>
          ))}
        </select>

        <select
          aria-label="City"
          title={filters.state_code ? 'Cities in the selected state' : 'All cities'}
          value={filters.city}
          onChange={(e) => handleFilterChange('city', e.target.value)}
          className="w-full sm:w-auto px-3 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
        >
          <option value="">All Cities</option>
          {cities.map((c) => (
            <option key={c.city} value={c.city}>{c.city}</option>
          ))}
        </select>

        <select
          value={filters.school_type}
          onChange={(e) => handleFilterChange('school_type', e.target.value)}
          className="w-full sm:w-auto px-3 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
        >
          <option value="">All Types</option>
          {SCHOOL_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-400 hover:text-red-500 hover:border-red-300 dark:hover:text-red-400 dark:hover:border-red-500/40 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            title="Clear all filters"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700 rounded-2xl min-w-0">
        {error ? (
          <div className="px-4 sm:px-6 py-12 text-center text-red-500 text-sm">{error}</div>
        ) : loading && schools.length === 0 ? (
          <Loader text="Loading schools..." />
        ) : schools.length === 0 ? (
          <div className="px-4 sm:px-6 py-12 sm:py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <p className="text-gray-900 dark:text-white font-semibold">No schools found</p>
            <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto overflow-y-visible max-w-full min-w-0">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/80">
                    <th className="px-3 sm:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">School</th>
                    <th className="px-3 sm:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider hidden sm:table-cell">District / LEA</th>
                    <th className="px-3 sm:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">State</th>
                    <th className="px-3 sm:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell">City</th>
                    <th className="px-3 sm:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider hidden md:table-cell">Type</th>
                    <th className="px-3 sm:px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider hidden sm:table-cell">Status</th>
                    <th className="sticky right-0 z-20 px-3 sm:px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider w-20 min-w-[5rem] bg-gray-50/95 dark:bg-slate-800/95 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)] dark:shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.35)]">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60">
                  {schools.map((s) => (
                    <tr key={s.id} className="group hover:bg-gray-50/60 dark:hover:bg-slate-700/20 transition-colors">
                      <td className="px-3 sm:px-5 py-3 sm:py-4">
                        <Link to={`/schools/${s.id}`} className="flex items-center gap-3 group min-w-0">
                          <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <p className="text-gray-900 dark:text-white font-semibold text-sm truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                              {s.school_name}
                            </p>
                            <p className="text-gray-500 dark:text-slate-400 text-xs truncate sm:hidden">
                              {formatStateLabel(s.state_code, stateMap)}
                            </p>
                          </div>
                        </Link>
                      </td>

                      <td className="px-3 sm:px-5 py-3 sm:py-4 hidden sm:table-cell">
                        <span className="text-gray-600 dark:text-slate-300 text-sm truncate block max-w-[160px]">
                          {s.district_name || '—'}
                        </span>
                      </td>

                      <td className="px-3 sm:px-5 py-3 sm:py-4">
                        <span className="text-gray-600 dark:text-slate-300 text-sm whitespace-nowrap">
                          {formatStateLabel(s.state_code, stateMap)}
                        </span>
                      </td>

                      <td className="px-3 sm:px-5 py-3 sm:py-4 hidden md:table-cell">
                        <span className="text-gray-600 dark:text-slate-300 text-sm truncate block max-w-[140px]">
                          {s.city || '—'}
                        </span>
                      </td>

                      <td className="px-3 sm:px-5 py-3 sm:py-4 hidden md:table-cell">
                        <span className="px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 text-xs font-medium border border-slate-200 dark:border-slate-600">
                          {s.school_type || '—'}
                        </span>
                      </td>

                      <td className="px-3 sm:px-5 py-3 sm:py-4 hidden sm:table-cell">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${s.is_active ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800' : 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800'}`}>
                          {s.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      <td className="sticky right-0 z-10 px-3 sm:px-4 py-3 sm:py-4 text-center bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm group-hover:bg-gray-50/95 dark:group-hover:bg-slate-700/40 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)] dark:shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.35)]">
                        <TableActionMenu
                          isOpen={openMenuId === s.id}
                          onToggle={() => toggleMenu(s.id)}
                          onClose={() => setOpenMenuId(null)}
                        >
                          <button
                            type="button"
                            onClick={() => { setOpenMenuId(null); navigate(`/schools/${s.id}`); }}
                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                          >
                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Details
                          </button>
                          <button
                            type="button"
                            onClick={() => { setOpenMenuId(null); navigate(`/schools/${s.id}/edit`); }}
                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                          >
                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit School
                          </button>
                          <div className="my-1 border-t border-gray-100 dark:border-slate-700" />
                          <button
                            type="button"
                            onClick={() => { setOpenMenuId(null); setConfirmDelete(s); }}
                            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete School
                          </button>
                        </TableActionMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.total_pages > 1 && (
              <div className="px-3 sm:px-5 py-3 sm:py-4 border-t border-gray-100 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Showing{' '}
                  <span className="font-medium text-gray-700 dark:text-slate-300">
                    {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium text-gray-700 dark:text-slate-300">{pagination.total}</span>
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let page;
                    if (pagination.total_pages <= 5) page = i + 1;
                    else if (pagination.page <= 3) page = i + 1;
                    else if (pagination.page >= pagination.total_pages - 2) page = pagination.total_pages - 4 + i;
                    else page = pagination.page - 2 + i;
                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors ${
                          page === pagination.page
                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                            : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.total_pages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
